/**
 * Utility functions for Return Order functionality
 */

import CheckCircleIcon from "@mui/icons-material/CheckCircle"
import WarningIcon from "@mui/icons-material/Warning"
import InfoIcon from "@mui/icons-material/Info"
/**
 * Get the appropriate color for a status chip
 * @param status - The status string to evaluate
 * @returns Material-UI color value
 */
export function getStatusColor(
  status: string
): "default" | "primary" | "secondary" | "success" | "error" | "info" | "warning" {
  const normalized = status.toLowerCase()

  if (["completed", "success", "approved"].includes(normalized)) return "success"
  if (["pending", "awaiting", "requested"].includes(normalized)) return "warning"
  if (["processing", "in_progress"].includes(normalized)) return "info"
  if (["rejected", "cancelled", "failed"].includes(normalized)) return "error"

  return "default"
}

/**
 * Get the appropriate icon for a status
 * @param status - The status string to evaluate
 * @returns React component for the status icon
 */
export function getStatusIcon(status: string) {
  const normalized = status.toLowerCase()

  if (["completed", "success", "approved"].includes(normalized)) {
    return <CheckCircleIcon fontSize="small" />
  }
  if (["pending", "awaiting", "requested"].includes(normalized)) {
    return <WarningIcon fontSize="small" />
  }
  if (["processing", "in_progress"].includes(normalized)) {
    return <InfoIcon fontSize="small" />
  }

  return <InfoIcon fontSize="small" />
}

/**
 * Generate mock data for testing purposes
 * @param id - The ID to use for generating mock data
 * @returns Mock return order detail object
 */
export function generateMockDetail(id: string) {
  const now = new Date()

  const item1 = {
    id: "itm_1",
    productId: "prd_001",
    name: "Wireless Mouse",
    description: "Ergonomic 2.4G wireless mouse",
    image: "https://via.placeholder.com/40x40.png?text=M",
    unitPrice: 25_00,
    quantity: 2,
    discount: 0,
    subTotal: 50_00,
    totalPrice: 50_00,
    lots: [
      { quantity: 1, actionId: "act-1001" },
      { quantity: 1, actionId: "act-1002" }
    ]
  }

  const item2 = {
    id: "itm_2",
    productId: "prd_002",
    name: "Mechanical Keyboard",
    description: "TKL, Blue Switches",
    image: "https://via.placeholder.com/40x40.png?text=K",
    unitPrice: 79_00,
    quantity: 1,
    discount: 10_00,
    subTotal: 79_00,
    totalPrice: 69_00,
    lots: [{ quantity: 1, actionId: "act-2001" }]
  }

  const totalAmount = item1.totalPrice + item2.totalPrice

  return {
    id,
    secondaryId: `RET-${id.slice(0, 6).toUpperCase()}`,
    parentOrderId: `ORD-${id.slice(-6).toUpperCase()}`,
    customerId: "cus_123",
    status: "completed",
    comment: "Customer returned due to wrong size.",
    totalAmount,
    createdAt: now.toISOString(),
    updatedAt: now.toISOString(),
    orderbyUser: { id: "usr_1", secondaryId: "U-0001", name: "Alex Johnson" },
    register: { id: "reg_1", secondaryId: "R-0003", name: "Front Desk" },
    items: [item1, item2]
  }
}
